import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { MapPin, Loader2, X } from "lucide-react";

/** Shared, standardised UK location picker used by cleaner service areas AND customer search. */
export const LocationAutocomplete = ({ onSelect, placeholder = "Start typing a town, city or county, e.g. Birmingham", testid = "location-autocomplete", value, onClear }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get("/locations/search", { params: { q: q.trim() } });
        setResults(data.results || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setBusy(false); }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [q]);

  const pick = (item) => {
    onSelect(item);
    setQ("");
    setResults([]);
    setOpen(false);
  };

  if (value) {
    return (
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-accent/40 bg-accent/5 text-sm" data-testid={`${testid}-selected`}>
        <MapPin className="h-4 w-4 text-accent shrink-0" />
        <span className="truncate font-medium">{value.label}</span>
        <button type="button" onClick={onClear} className="ml-auto text-muted-foreground hover:text-destructive" data-testid={`${testid}-clear`}>
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" data-testid={testid}>
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border bg-background focus-within:border-accent">
        <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => setOpen(true)}
          placeholder={placeholder} className="flex-1 bg-transparent outline-none text-sm"
          data-testid={`${testid}-input`} />
        {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      {open && results.length > 0 && (
        <ul className="absolute z-30 left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg overflow-hidden max-h-64 overflow-y-auto" data-testid={`${testid}-results`}>
          {results.map((r) => (
            <li key={r.place_id}>
              <button type="button" onClick={() => pick(r)}
                className="w-full text-left px-3 py-2.5 text-sm hover:bg-secondary transition-colors"
                data-testid={`${testid}-option`}>
                {r.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && q.trim().length >= 2 && !busy && results.length === 0 && (
        <p className="text-xs text-muted-foreground mt-1" data-testid={`${testid}-empty`}>
          No matching UK place found — try the nearest larger town, city or the county.
        </p>
      )}
    </div>
  );
};

export default LocationAutocomplete;
